import { Injectable, ElementRef } from '@angular/core';

/**
 * Handles scroll-to-top behaviour on pagination.
 *
 * Admin pages render their content inside an inner `overflow-y: auto` container
 * (e.g. `calc(100vh - 80px)` wrappers) rather than relying on the window scroll.
 * This service walks up the DOM from the paginating component to find the nearest
 * scrollable ancestor and scrolls it to the top, falling back to `window` when no
 * such ancestor exists.
 */
@Injectable({ providedIn: 'root' })
export class PaginationScrollService {
  /**
   * Scroll to the top of the nearest scrollable ancestor of the given element,
   * or fall back to `window.scrollTo` when none is found.
   *
   * @param elementRef - An Angular ElementRef from the component that triggered
   *                     the page change. Pass `null` to skip DOM traversal and
   *                     only attempt a window scroll.
   */
  scrollToTop(elementRef: ElementRef | null = null): void {
    const scrollable = elementRef
      ? this.findScrollableAncestor(elementRef.nativeElement)
      : null;

    if (scrollable) {
      scrollable.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  /**
   * Walk up the DOM tree from `el` and return the first ancestor whose content
   * overflows vertically (i.e. has a non-trivial `overflow-y` and is actually
   * scrolled or tall enough to scroll).
   */
  private findScrollableAncestor(el: HTMLElement): HTMLElement | null {
    let current = el.parentElement;

    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      const isScrollable = overflowY === 'auto' || overflowY === 'scroll';

      if (isScrollable && current.scrollHeight > current.clientHeight) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }
}
